import { Customer } from "../types/customer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus, RefreshCw } from "lucide-react";
import { formatDateTime, type TimezoneMode } from "../utils/dates";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface CustomerTableProps {
  customers: Customer[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onAdd: () => void;
  onRefresh: () => void;
  onScanAll: () => void;
  onDryRunAll: () => void;
  onCancel: () => void;
  onClearAllResults: () => void;
  onOpen: (customer: Customer) => void;
  onMore: (customer: Customer) => void;
  leadFilter: "all" | "customers" | "leads";
  onLeadFilterChange: (filter: "all" | "customers" | "leads") => void;
  sortMode: "default" | "recent";
  onSortModeChange: (mode: "default" | "recent") => void;
  timezone: TimezoneMode;
}

function StatusBadge({ status }: { status: Customer["status"] }) {
  const variants: Record<Customer["status"], { label: string; variant: "neutral" | "info" | "success" | "danger" }> = {
    idle: { label: "idle", variant: "neutral" },
    scanning: { label: "scanning", variant: "info" },
    completed: { label: "completed", variant: "success" },
    error: { label: "last error", variant: "danger" },
    no_scans: { label: "no scans", variant: "neutral" },
  };

  const variant = variants[status];

  return (
    <Badge variant={variant.variant} className={status === "scanning" ? "animate-pulse" : undefined}>
      {variant.label}
    </Badge>
  );
}

function SourceBadge({ source }: { source?: Customer["source"] }) {
  const normalized = source === "free_scan" ? "free_scan" : "customer";
  const label = normalized === "free_scan" ? "Free Scan (To Contact)" : "Customers";
  const variant = normalized === "free_scan" ? "warning" : "info";

  return <Badge variant={variant}>{label}</Badge>;
}

function isLeadCustomer(customer: Customer) {
  return customer.source === "free_scan" || customer.lead === true;
}

function isEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

function normalizeTags(tags?: string[]) {
  if (!Array.isArray(tags)) return [];
  return tags.map((tag) => String(tag || "").trim()).filter(Boolean);
}

function recentTimestamp(customer: Customer) {
  return (
    customer.leadLastSeenAt ||
    customer.leadCreatedAt ||
    customer.updatedAt ||
    customer.createdAt ||
    customer.lastScan ||
    ""
  );
}

export function CustomerTable({
  customers,
  searchQuery,
  onSearchChange,
  onAdd,
  onRefresh,
  onScanAll,
  onDryRunAll,
  onCancel,
  onClearAllResults,
  onOpen,
  onMore,
  leadFilter,
  onLeadFilterChange,
  sortMode,
  onSortModeChange,
  timezone,
}: CustomerTableProps) {
  const filteredCustomers = customers
    .filter((c) => {
      const q = searchQuery.toLowerCase();
      const matches =
        c.name.toLowerCase().includes(q) ||
        c.url.toLowerCase().includes(q) ||
        c.contact.toLowerCase().includes(q) ||
        String(c.leadEmail || "").toLowerCase().includes(q);
      if (!matches) return false;
      const isLead = isLeadCustomer(c);
      if (leadFilter === "leads") return isLead;
      if (leadFilter === "customers") return !isLead;
      return true;
    })
    .sort((a, b) => {
      if (sortMode !== "recent") return 0;
      const at = recentTimestamp(a);
      const bt = recentTimestamp(b);
      if (!at && !bt) return 0;
      if (!at) return 1;
      if (!bt) return -1;
      return String(bt).localeCompare(String(at));
    });

  return (
    <div className="flex-1">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-mono font-bold">Customers</h2>
        
        <div className="flex items-center gap-3">
          <Select value={leadFilter} onValueChange={(value) => onLeadFilterChange(value as CustomerTableProps["leadFilter"])}>
            <SelectTrigger className="w-[170px]">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="customers">Customers</SelectItem>
              <SelectItem value="leads">Leads (Live Scan)</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortMode} onValueChange={(value) => onSortModeChange(value as CustomerTableProps["sortMode"])}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default</SelectItem>
              <SelectItem value="recent">Recently seen</SelectItem>
            </SelectContent>
          </Select>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search customers (name or URL)"
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              className="w-64 pl-10"
            />
          </div>
          
          <Button onClick={onAdd} size="sm" variant="secondary">
            <Plus className="mr-1 h-4 w-4" />
            Add
          </Button>
          <Button onClick={onRefresh} size="sm" variant="secondary">
            <RefreshCw className="mr-1 h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={onScanAll} size="sm">
            Scan all
          </Button>
          <Button onClick={onDryRunAll} size="sm" variant="secondary">
            Dry run all
          </Button>
          <Button onClick={onCancel} size="sm" variant="destructive">
            Cancel
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="secondary">
                Clear results
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear all results and logs?</AlertDialogTitle>
                <AlertDialogDescription>
                  This deletes all saved scan results and logs for all customers.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onClearAllResults}>Clear</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="cyber-card overflow-hidden">
        <table className="w-full">
          <thead className="border-b border-border bg-muted/30">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-primary">Name</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-primary">URL</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-primary">Contact</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-primary">Last scan</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-primary">Status</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-primary">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">
                  No customers match your search.
                </td>
              </tr>
            )}
            {filteredCustomers.map((customer) => (
              <tr key={customer.id} className="border-b border-border/50 hover:bg-muted/10 transition-colors">
                <td className="px-4 py-4 text-sm text-foreground">{customer.name}</td>
                <td className="px-4 py-4 text-sm text-primary">
                  <a
                    href={customer.url}
                    target="_blank"
                    rel="noreferrer"
                    className="underline underline-offset-2 hover:opacity-90"
                  >
                    {customer.url}
                  </a>
                </td>
                <td className="px-4 py-4 text-sm text-primary">
                  <div className="flex flex-col gap-1">
                    <span>{customer.contact}</span>
                    {(() => {
                      const leadEmail = customer.leadEmail || (isEmail(customer.contact) ? customer.contact : "");
                      if (!leadEmail || leadEmail === customer.contact) return null;
                      return <span className="text-xs text-muted-foreground">{leadEmail}</span>;
                    })()}
                  </div>
                </td>
                <td className="px-4 py-4 text-sm text-muted-foreground">
                  {formatDateTime(customer.lastScan, timezone)}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <SourceBadge source={customer.source} />
                    {isLeadCustomer(customer) && (
                      <Badge variant="warning">
                        Live Scan Lead
                      </Badge>
                    )}
                    {normalizeTags(customer.tags).map((tag) => (
                      <Badge key={`${customer.id}-${tag}`} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    <Badge
                      variant={customer.enabled ? "success" : "neutral"}
                    >
                      {customer.enabled ? "enabled" : "disabled"}
                    </Badge>
                    <StatusBadge status={customer.status} />
                    {customer.status === "no_scans" && (
                      <span className="h-2 w-2 rounded-full bg-muted-foreground animate-pulse" />
                    )}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      size="sm"
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                      onClick={() => onOpen(customer)}
                    >
                      Open
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onMore(customer)}
                    >
                      More
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
